import { Col, Row, Space, theme } from 'antd';
import Text from 'antd/lib/typography';
import {
	ProductKeys,
	ProductSwitcher,
} from '@copilot/common/pages/layouts/headerSider/header/productSwitcher';
import { SupportDropdown } from '@copilot/common/pages/layouts/headerSider/header/supportDropdown';
import { UserDropdown } from '@copilot/common/pages/layouts/headerSider/header/userDropdown';
import { useSelector } from 'react-redux';
import { OrganizationMemberSelectors } from '@copilot/common/store/selectors/organizationMember';
import { isNil } from 'lodash';
import { Config } from '@copilot/common/config';
import { handleUserLogout } from '@copilot/common/utils';

const { useToken } = theme;

type AccountDashboardHeaderPropsType = {
	/**
	 * User's name
	 */
	name: string;

	/**
	 * Callback for handling open help portal
	 */
	handleOpenHelpPortal?: () => void;

	/**
	 * Callback for handling report an issue
	 */
	handleReportAnIssue?: () => void;

	/**
	 * Callback for handling user logout
	 */
	handleLogout: () => void;
};

/**
 * Represents the header component of the account dashboard.
 * @constructor
 * @returns {JSX.Element} The AccountDashboardHeader component.
 */
function AccountDashboardHeader(props: AccountDashboardHeaderPropsType): JSX.Element {
	const { handleOpenHelpPortal, handleReportAnIssue, handleLogout, name } = props;
	const { token } = useToken();

	return (
		<Row align={'middle'} justify={'space-between'} style={{ userSelect: 'none' }}>
			<Col>
				<Space size="large">
					<Text style={{ fontSize: token.fontSizeLG }}>Account Dashboard</Text>
				</Space>
			</Col>
			<Col>
				<Space size="small">
					<ProductSwitcher currentProductKey={ProductKeys.accountDashboard} />
					<SupportDropdown
						handleOpenHelpPortal={handleOpenHelpPortal}
						handleReportAnIssue={handleReportAnIssue}
					/>
					<UserDropdown name={name} handleLogout={handleLogout} />
				</Space>
			</Col>
		</Row>
	);
}

/**
 * [Smart] Component to handle logic for the account dashboard header
 *
 * @returns {JSX.Element | null} The rendered React component or null if there is no active member.
 */
export function AccountDashboardHeaderContainer(): JSX.Element | null {
	const activeMember = useSelector(OrganizationMemberSelectors.getActiveMember);
	if (isNil(activeMember)) return null;

	return (
		<AccountDashboardHeader
			name={activeMember.name}
			handleOpenHelpPortal={() => window.open(Config.helpPortalURL)}
			handleReportAnIssue={() => window.open(Config.reportBugURL)}
			handleLogout={() => handleUserLogout(Config.isB2CEnabled)}
		/>
	);
}
